import { Controller } from "@hotwired/stimulus";
import { createRoot } from "react-dom/client";
import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ProfilePicker } from "./profile-picker";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

interface Profile {
  id: number;
  name: string;
  slug?: string;
  avatar_url?: string;
  [key: string]: unknown;
}

export default class extends Controller {
  static targets = ["container"];

  declare containerTarget: HTMLElement;

  private root: ReturnType<typeof createRoot> | null = null;

  connect() {
    if (!this.root) {
      this.root = createRoot(this.containerTarget);
      this.render();
    }
  }

  disconnect() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  private render() {
    if (!this.root) return;

    const profileType = this.element.dataset.profileType || "";
    const searchUrl = this.element.dataset.searchUrl || "";
    const mode = (this.element.dataset.mode || "single") as
      | "single"
      | "multiple";
    const name = this.element.dataset.name || "";
    const selectedJson = this.element.dataset.selected || "null";
    const allowInvite = this.element.dataset.allowInvite === "true";
    const label = this.element.dataset.label || "";
    const sourceType = this.element.dataset.sourceType;
    const sourceId = this.element.dataset.sourceId;

    let selectedValue: Profile | Profile[] | null = null;
    try {
      selectedValue = JSON.parse(selectedJson) as Profile | Profile[] | null;
    } catch (e) {
      selectedValue = null;
    }

    this.root.render(
      <QueryClientProvider client={queryClient}>
        <ProfilePicker
          profileType={profileType}
          searchUrl={searchUrl}
          mode={mode}
          name={name}
          initialValue={selectedValue}
          allowInvite={allowInvite}
          label={label}
          sourceType={sourceType}
          sourceId={sourceId}
        />
      </QueryClientProvider>,
    );
  }
}
